import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import toast from 'react-hot-toast';

const BatchedUpdatesContext = createContext();

/**
 * Provider for global batched updates state
 * Manages optimistic updates at app level so they survive navigation
 */
export const BatchedUpdatesProvider = ({ children, debounceMs = 250 }) => {
    // Store pending updates: { jobId: { field1: value1, field2: value2 } }
    const [pendingUpdates, setPendingUpdates] = useState({});

    // Track which jobs have unsaved changes
    const [unsavedJobs, setUnsavedJobs] = useState(new Set());

    // Debounce timer ref
    const debounceTimerRef = useRef(null);

    // Track active save operations
    const [isSaving, setIsSaving] = useState(false);

    /**
     * Flush all pending updates to Firestore
     */
    const flushUpdates = useCallback(async () => {
        const updates = { ...pendingUpdates };
        const jobIds = Object.keys(updates);

        if (jobIds.length === 0) {
            return;
        }

        setIsSaving(true);

        try {
            // Execute all updates in parallel
            const updatePromises = jobIds.map(async (jobId) => {
                const jobRef = doc(db, 'jobs', jobId);
                await updateDoc(jobRef, updates[jobId]);
                return { jobId, success: true, fieldCount: Object.keys(updates[jobId]).length };
            });

            await Promise.all(updatePromises);

            // Clear pending updates for successfully saved jobs
            setPendingUpdates(prev => {
                const newPending = { ...prev };
                jobIds.forEach((jobId) => {
                    delete newPending[jobId];
                });
                return newPending;
            });

            // Clear unsaved jobs
            setUnsavedJobs(prev => {
                const newSet = new Set(prev);
                jobIds.forEach((jobId) => {
                    newSet.delete(jobId);
                });
                return newSet;
            });

            // Show subtle success notification (bottom-right, 1 second)
            toast.success('Saved ✓', {
                duration: 1000,
                position: 'bottom-right',
                style: {
                    background: '#10B981',
                    color: 'white',
                    fontSize: '14px',
                    padding: '8px 16px',
                    borderRadius: '6px'
                }
            });

        } catch (error) {
            console.error('Error saving batch updates:', error);
            toast.error('Failed to save changes. Please try again.');
        } finally {
            setIsSaving(false);
        }
    }, [pendingUpdates]);

    /**
     * Add a field update to the pending batch
     * Provides instant optimistic update
     */
    const queueUpdate = useCallback((jobId, field, value) => {
        setPendingUpdates(prev => ({
            ...prev,
            [jobId]: {
                ...(prev[jobId] || {}),
                [field]: value
            }
        }));

        setUnsavedJobs(prev => new Set([...prev, jobId]));

        // Clear existing timer and start new one
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set new debounce timer
        debounceTimerRef.current = setTimeout(() => {
            flushUpdates();
        }, debounceMs);
    }, [debounceMs, flushUpdates]);

    /**
     * Get the current value for a field (from pending updates or original)
     */
    const getCurrentValue = useCallback((jobId, field, originalValue) => {
        if (pendingUpdates[jobId]?.[field] !== undefined) {
            return pendingUpdates[jobId][field];
        }
        return originalValue;
    }, [pendingUpdates]);

    /**
     * Check if a job has unsaved changes
     */
    const hasUnsavedChanges = useCallback((jobId) => {
        return unsavedJobs.has(jobId);
    }, [unsavedJobs]);

    /**
     * Cancel pending updates for a specific job
     */
    const cancelUpdates = useCallback((jobId) => {
        setPendingUpdates(prev => {
            const newPending = { ...prev };
            delete newPending[jobId];
            return newPending;
        });

        setUnsavedJobs(prev => {
            const newSet = new Set(prev);
            newSet.delete(jobId);
            return newSet;
        });

        // If no more pending updates, clear the timer
        const remainingJobs = Object.keys(pendingUpdates).filter(id => id !== jobId);
        if (remainingJobs.length === 0 && debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    }, [pendingUpdates]);

    /**
     * Immediately flush updates without waiting for debounce
     */
    const saveNow = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        flushUpdates();
    }, [flushUpdates]);

    /**
     * Mark a skip as collected with all required fields
     * This is a convenience method that queues multiple fields at once
     */
    const markAsCollected = useCallback((jobId, collectedBy) => {
        const timestamp = new Date().toISOString();
        const collectionDate = new Date().toLocaleDateString();

        // Queue all collection-related fields as a batch
        setPendingUpdates(prev => ({
            ...prev,
            [jobId]: {
                ...(prev[jobId] || {}),
                status: 'collected',
                collectionTimestamp: timestamp,
                collectionDate: collectionDate,
                collectedBy: collectedBy || 'unknown'
            }
        }));

        setUnsavedJobs(prev => new Set([...prev, jobId]));

        // Clear existing timer and start new one
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set new debounce timer
        debounceTimerRef.current = setTimeout(() => {
            flushUpdates();
        }, debounceMs);
    }, [debounceMs, flushUpdates]);

    // Cleanup on unmount - save any pending updates
    useEffect(() => {
        return () => {
            // Clear the debounce timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // CRITICAL: Flush pending updates before unmounting
            // This ensures changes are saved when app closes
            const updates = { ...pendingUpdates };
            const jobIds = Object.keys(updates);

            if (jobIds.length > 0) {
                // Execute saves synchronously (component is unmounting, no state updates)
                jobIds.forEach(async (jobId) => {
                    try {
                        const jobRef = doc(db, 'jobs', jobId);
                        await updateDoc(jobRef, updates[jobId]);
                    } catch (error) {
                        console.error('Error saving on unmount:', error);
                    }
                });
            }
        };
    }, [pendingUpdates]);

    const value = {
        queueUpdate,
        getCurrentValue,
        hasUnsavedChanges,
        cancelUpdates,
        saveNow,
        markAsCollected,
        isSaving,
        pendingCount: Object.keys(pendingUpdates).length,
        unsavedJobIds: Array.from(unsavedJobs)
    };

    return (
        <BatchedUpdatesContext.Provider value={value}>
            {children}
        </BatchedUpdatesContext.Provider>
    );
};

/**
 * Hook to use batched updates from anywhere in the app
 */
export const useBatchedUpdates = () => {
    const context = useContext(BatchedUpdatesContext);
    if (!context) {
        throw new Error('useBatchedUpdates must be used within a BatchedUpdatesProvider');
    }
    return context;
};
